import {
  Alert,
  Button,
  Checkbox,
  Group,
  Icon,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Text,
} from "@assetwatch/ui";
import type { ReactNode } from "react";
import type { ProcedureExecutionItem } from "../../work-orders/ProcedureExecutionLayout";
import type { ItemSaveStatus } from "../../work-orders/useProcedureItemAutosave";

type Answer = string | boolean | number | string[] | undefined;

export type ProcedurePhotoFieldValidation = {
  error: boolean;
  errorDescriptionId?: string;
};

/** Exponent and sign-prefix characters a number input accepts but a reading never needs. */
const blockedNumericKeys = new Set(["e", "E", "+"]);
const numericText = /^-?\d*\.?\d*$/;

function ResponseControl({
  item,
  answer,
  onBlur,
  onChange,
  readOnly,
  unresolved,
  errorId,
}: {
  item: ProcedureExecutionItem;
  answer: Answer;
  onBlur: () => void;
  onChange: (value: string | boolean | string[]) => void;
  readOnly: boolean;
  unresolved: boolean;
  errorId: string;
}) {
  if (item.type === "single-choice") {
    return (
      <fieldset
        className={
          unresolved
            ? "rounded-(--radius-sm) border border-input-error-outline p-(--spacing-8)"
            : "m-0 border-0 p-0"
        }
      >
        <legend className="sr-only">{item.label}</legend>
        <RadioGroup
          aria-describedby={unresolved ? errorId : undefined}
          aria-label={item.label}
          aria-required={item.required || undefined}
          disabled={readOnly}
          error={unresolved || undefined}
          onValueChange={(value) => {
            if (value !== null) onChange(value);
          }}
          value={typeof answer === "string" ? answer : ""}
        >
          {(item.options ?? []).map((option) => (
            <Radio key={option} label={option} value={option} />
          ))}
        </RadioGroup>
      </fieldset>
    );
  }

  if (item.type === "text") {
    return (
      <textarea
        aria-describedby={unresolved ? errorId : undefined}
        aria-invalid={unresolved || undefined}
        aria-label={item.label}
        aria-required={item.required || undefined}
        className={
          unresolved
            ? "min-h-(--spacing-64) w-full max-w-md resize-y rounded-(--radius-sm) border border-input-error-outline bg-input-default-background px-(--spacing-10) py-(--spacing-8) font-(family-name:--font-family-body) text-(length:--font-size-body-medium) text-input-text-default outline-none placeholder:text-text-subtle focus:border-input-error-outline"
            : "min-h-(--spacing-64) w-full max-w-md resize-y rounded-(--radius-sm) border border-input-default-outline bg-input-default-background px-(--spacing-10) py-(--spacing-8) font-(family-name:--font-family-body) text-(length:--font-size-body-medium) text-input-text-default outline-none placeholder:text-text-subtle focus:border-input-active-outline"
        }
        onBlur={onBlur}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Free text response"
        readOnly={readOnly}
        value={typeof answer === "string" ? answer : ""}
      />
    );
  }

  const value =
    typeof answer === "string" || typeof answer === "number" ? answer : "";

  if (item.type === "numeric") {
    return (
      // @ds-deviation: the POC numeric field is 140px wide and the spacing scale has no 140; 160 is the nearest token that still fits the unit — promote a 140 width token into the design system
      <div
        className={
          unresolved
            ? "w-(--spacing-160) [&>div>div]:border-input-error-outline [&>div>div:focus-within]:border-input-error-outline"
            : "w-(--spacing-160)"
        }
      >
        <Input
          aria-describedby={unresolved ? errorId : undefined}
          aria-invalid={unresolved || undefined}
          aria-required={item.required || undefined}
          // The DS Input renders the unit as an unassociated sibling, so name
          // it here for assistive technology.
          aria-label={item.unit ? `${item.label} (${item.unit})` : item.label}
          disabled={readOnly}
          inputMode="decimal"
          onBlur={onBlur}
          onChange={(event) => {
            // A number input reports "" while its text is not a valid number
            // (for example "1e"). Treating that as a cleared answer would
            // erase the saved reading on blur, so keep the current answer.
            if (event.target.value === "" && event.target.validity.badInput)
              return;
            onChange(event.target.value);
          }}
          onKeyDown={(event) => {
            if (blockedNumericKeys.has(event.key)) event.preventDefault();
          }}
          onPaste={(event) => {
            if (!numericText.test(event.clipboardData.getData("text")))
              event.preventDefault();
          }}
          placeholder="0"
          readOnly={readOnly}
          type="number"
          // @ds-deviation: the POC shows the unit beside the field, the DS Input renders it inside the field — promote an external unit position into the design system or accept the DS-native placement
          unit={item.unit}
          value={value}
        />
      </div>
    );
  }

  return (
    <Input
      aria-describedby={unresolved ? errorId : undefined}
      aria-invalid={unresolved || undefined}
      aria-label={item.label}
      aria-required={item.required || undefined}
      disabled={readOnly}
      onBlur={onBlur}
      onChange={(event) => onChange(event.target.value)}
      readOnly={readOnly}
      type="text"
      value={value}
    />
  );
}

function describeValue(
  value: Extract<ItemSaveStatus, { kind: "conflict" }>["current"]["value"],
) {
  if (!value) return "No response";
  if (value.type === "CHECKBOX") return value.value ? "Checked" : "Unchecked";
  if (value.type === "PHOTO") return `${value.fileIds.length} photos`;
  return String(value.value);
}

function formatSavedAt(value: string | null) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return null;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

/** What the autosave line says: Saving, Saved, or what went wrong. */
function SaveStatusContent({
  status,
  onRetry,
}: {
  status: ItemSaveStatus | undefined;
  onRetry: () => void;
}) {
  if (status?.kind === "saving")
    return (
      <Text size="sm" tone="subtle">
        Saving…
      </Text>
    );
  if (status?.kind === "saved")
    return (
      <Group align="center" gap={4}>
        <Icon name="check-mark" size="xs" tone="positive" />
        <Text size="sm" tone="subtle">
          Saved
        </Text>
      </Group>
    );
  if (
    status?.kind === "invalid" ||
    (status?.kind === "conflict" && status.message)
  )
    return (
      <Text size="sm" tone="negative">
        {status.message}
      </Text>
    );
  if (status?.kind === "error")
    return (
      <Group align="center" gap={8}>
        <Text size="sm" tone="negative">
          Not saved.
        </Text>
        <Button onClick={onRetry} size="sm" variant="outline">
          Retry
        </Button>
      </Group>
    );
  return null;
}

/**
 * The per-item autosave line. An output element is a polite live region; it is
 * always mounted and only its content changes, so a screen reader announces
 * each change, including a failed save. With no content it takes no space.
 */
function SaveStatusLine(props: {
  status: ItemSaveStatus | undefined;
  onRetry: () => void;
}) {
  return (
    <output className="block empty:hidden">
      <SaveStatusContent {...props} />
    </output>
  );
}

/**
 * Shown when another user saved a different answer to this item first. The
 * field keeps what the user typed until they choose.
 */
function ConflictNotice({
  status,
  onKeepMine,
  onUseTheirs,
}: {
  status: Extract<ItemSaveStatus, { kind: "conflict" }>;
  onKeepMine: () => void;
  onUseTheirs: () => void;
}) {
  const { current } = status;
  const savedAt = formatSavedAt(current.updatedAt);
  return (
    <Alert
      action={
        <>
          <Button onClick={onKeepMine} size="sm" variant="outline">
            Keep mine
          </Button>
          <Button onClick={onUseTheirs} size="sm" variant="outline">
            Use theirs
          </Button>
        </>
      }
      subtext={`Their response: ${describeValue(current.value)}${savedAt ? ` at ${savedAt}` : ""}`}
      title={`${current.updatedByName ?? "Another user"} saved a newer response`}
      tone="warning"
    />
  );
}

export function ProcedureExecutionItemCard({
  item,
  answer,
  onBlur,
  onChange,
  onKeepMine,
  onRetry,
  onUseTheirs,
  photoField,
  readOnly,
  status,
  unresolved = false,
}: {
  item: ProcedureExecutionItem;
  answer: Answer;
  /** A text or number field lost focus. */
  onBlur: () => void;
  onChange: (value: string | boolean | string[]) => void;
  onKeepMine: () => void;
  onRetry: () => void;
  onUseTheirs: () => void;
  /** The response control for a PHOTO item, owned by the caller. */
  photoField?: (validation: ProcedurePhotoFieldValidation) => ReactNode;
  readOnly: boolean;
  /** Autosave state of this item; absent when answers are not saved. */
  status?: ItemSaveStatus;
  unresolved?: boolean;
}) {
  const requiredErrorId = `procedure-required-${item.id}`;

  return (
    <div className="rounded-(--radius-md) border border-outline-subtle bg-surface-container-low px-(--spacing-16) py-(--spacing-16)">
      <Stack gap={12}>
        <Group align="start" justify="between" wrap="wrap">
          <Group align="start" gap={8}>
            {item.type === "checkbox" ? (
              <fieldset
                className={
                  unresolved
                    ? "rounded-(--radius-sm) border border-input-error-outline p-(--spacing-4)"
                    : "m-0 border-0 p-0"
                }
              >
                <legend className="sr-only">{item.label}</legend>
                <Checkbox
                  aria-label={item.label}
                  aria-describedby={unresolved ? requiredErrorId : undefined}
                  aria-required={item.required || undefined}
                  checked={answer === true}
                  disabled={readOnly}
                  error={unresolved || undefined}
                  onCheckedChange={(checked) => onChange(Boolean(checked))}
                />
              </fieldset>
            ) : null}
            <Stack gap={4}>
              <Text weight="medium">
                {item.label}
                {item.required ? (
                  <span aria-hidden="true" className="text-input-error-text">
                    {" *"}
                  </span>
                ) : null}
              </Text>
              {item.description ? (
                <Text size="sm" tone="subtle">
                  {item.description}
                </Text>
              ) : null}
              {unresolved ? (
                <Text id={requiredErrorId} size="sm" tone="negative">
                  Response required
                </Text>
              ) : null}
              {item.type !== "photo" ? (
                <SaveStatusLine onRetry={onRetry} status={status} />
              ) : null}
            </Stack>
          </Group>
        </Group>
        {item.type === "photo" && photoField ? (
          <fieldset
            className={
              unresolved
                ? "min-w-0 rounded-(--radius-sm) border border-input-error-outline p-(--spacing-4)"
                : "m-0 min-w-0 border-0 p-0"
            }
          >
            <legend className="sr-only">{item.label}</legend>
            {photoField({
              error: unresolved,
              errorDescriptionId: unresolved ? requiredErrorId : undefined,
            })}
          </fieldset>
        ) : null}
        {item.type !== "checkbox" && item.type !== "photo" ? (
          <ResponseControl
            answer={answer}
            item={item}
            onBlur={onBlur}
            onChange={onChange}
            readOnly={readOnly}
            unresolved={unresolved}
            errorId={requiredErrorId}
          />
        ) : null}
        {status?.kind === "conflict" ? (
          <ConflictNotice
            onKeepMine={onKeepMine}
            onUseTheirs={onUseTheirs}
            status={status}
          />
        ) : null}
      </Stack>
    </div>
  );
}
